import { useCallback, useMemo } from "react";
import { TradeToEarnCampaign, TradeToEarnCampaignEpoch, hasWindowCooldown, WindowCooldownMode } from "@cede/types";
import { useCountdown } from "@/hooks/core/useCountdown";
import { useUserVolumeInWindow } from "@/hooks/volumeTracker";

type UseWindowCooldownProgressParams = {
  campaign?: TradeToEarnCampaign;
  currentEpoch?: TradeToEarnCampaignEpoch;
};

/**
 * Provides countdown and refetch helpers for window-based cooldown campaigns.
 */
export const useWindowCooldownProgress = ({ campaign, currentEpoch }: UseWindowCooldownProgressParams) => {
  const epochRule = currentEpoch?.rule?.data;

  const windowRule = useMemo(() => {
    if (!epochRule || !hasWindowCooldown(epochRule)) {
      return undefined;
    }
    if (epochRule.cooldown.mode === WindowCooldownMode.NO_COOLDOWN) {
      return undefined;
    }
    return epochRule;
  }, [epochRule]);

  const isWindowBasedCooldown = !!windowRule;

  const { userVolumeInWindow, nextWindowDate, refetchUserVolumeInWindow, isLoading } = useUserVolumeInWindow({
    campaignName: campaign?.name || "",
    epoch: currentEpoch,
    enabled: !!campaign && !!currentEpoch && isWindowBasedCooldown,
  });

  const handleWindowReset = useCallback(() => {
    if (isWindowBasedCooldown) {
      refetchUserVolumeInWindow();
    }
  }, [isWindowBasedCooldown, refetchUserVolumeInWindow]);

  const windowCountdownConfig = useMemo(
    () => ({
      onFinish: handleWindowReset,
    }),
    [handleWindowReset],
  );

  const windowResetTimeCountdown = useCountdown(
    isWindowBasedCooldown ? nextWindowDate : undefined,
    windowCountdownConfig,
  );

  return {
    epochRule: windowRule,
    isWindowBasedCooldown,
    userVolumeInWindow,
    nextWindowDate,
    windowResetTimeCountdown,
    isLoading,
  };
};
